<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbItems" />

    <div v-if="!isSuperUser" class="alert alert-danger">
      Acesso permitido apenas para usuário super.
    </div>

    <div v-else class="row">
      <div class="col-12 col-xl-5">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title mb-3">Usuários</h5>
            <div class="mb-3">
              <input
                v-model="userSearch"
                type="text"
                class="form-control"
                placeholder="Buscar por nome ou e-mail"
              />
            </div>

            <div class="list-group user-list-scroll">
              <button
                v-for="user in filteredUsers"
                :key="user.id"
                type="button"
                class="list-group-item list-group-item-action"
                :class="{ active: selectedUser && selectedUser.id === user.id }"
                @click="selectUser(user)"
              >
                <div class="fw-bold">{{ user.name }}</div>
                <small>{{ user.email }}</small>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-xl-7">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h5 class="card-title mb-0">
                Empresas vinculadas
              </h5>
              <button class="btn btn-outline-secondary btn-sm" @click="loadData">
                Atualizar
              </button>
            </div>

            <p v-if="selectedUser" class="text-muted mb-3">
              Usuário selecionado: <strong>{{ selectedUser.name }}</strong>
            </p>
            <div v-else class="alert alert-info">
              Selecione um usuário para gerenciar vínculos com empresas.
            </div>

            <div v-if="selectedUser" class="table-responsive">
              <table class="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Empresa</th>
                    <th>Fantasia</th>
                    <th class="text-end">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="empresa in empresas" :key="empresa.id">
                    <td>{{ empresa.apelido || '-' }}</td>
                    <td>{{ empresa.fantasia || empresa.razao || '-' }}</td>
                    <td class="text-end">
                      <div class="btn-group btn-group-sm">
                        <button
                          v-if="!isLinked(empresa.id)"
                          class="btn btn-outline-primary"
                          :disabled="saving"
                          @click="attachEmpresa(empresa.id)"
                        >
                          Vincular
                        </button>
                        <button
                          v-else
                          class="btn btn-outline-danger"
                          :disabled="saving"
                          @click="detachEmpresa(empresa.id)"
                        >
                          Remover
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="selectedUser" class="mt-3">
              <button
                class="btn btn-primary"
                :disabled="saving"
                @click="saveAll"
              >
                Salvar alterações (sync)
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/views/layouts/main';
import PageHeader from '@/components/page-header';
import { http } from '@/helpers/easyindustriaapi/config';
import Swal from 'sweetalert2';

export default {
  name: 'UserCompanyRelations',
  components: { Layout, PageHeader },
  data() {
    return {
      title: 'Usuário x Empresa',
      breadcrumbItems: [
        { text: 'Usuários', href: '#' },
        { text: 'Relacionamento Usuário/Empresa', active: true },
      ],
      users: [],
      empresas: [],
      selectedUser: null,
      selectedEmpresaIds: [],
      userSearch: '',
      loading: false,
      saving: false,
    };
  },
  computed: {
    currentUser() {
      try {
        return JSON.parse(localStorage.getItem('user') || 'null');
      } catch (e) {
        return null;
      }
    },
    isSuperUser() {
      const roles = (this.currentUser && this.currentUser.roles) || [];
      return roles.some((r) => r && r.name === 'super');
    },
    filteredUsers() {
      const query = (this.userSearch || '').trim().toLowerCase();
      if (!query) return this.users;
      return this.users.filter((u) =>
        `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(query)
      );
    },
  },
  mounted() {
    if (!this.isSuperUser) {
      this.showAlert('error', 'Acesso negado', 'Somente usuário super pode acessar esta página.');
      this.$router.replace({ name: 'home' });
      return;
    }
    this.loadData();
  },
  methods: {
    showAlert(icon, title, text) {
      return Swal.fire({ icon, title, text });
    },
    isLinked(empresaId) {
      return this.selectedEmpresaIds.includes(Number(empresaId));
    },
    selectUser(user) {
      this.selectedUser = user;
      this.selectedEmpresaIds = (user.empresas || []).map((e) => Number(e.id));
    },
    async loadData() {
      this.loading = true;
      try {
        const { data } = await http.get('/user-company');
        this.users = data.users || [];
        this.empresas = data.empresas || [];
        if (this.users.length > 0) {
          this.selectUser(this.users[0]);
        }
      } catch (e) {
        this.showAlert('error', 'Erro', 'Falha ao carregar relacionamento Usuário/Empresa.');
      } finally {
        this.loading = false;
      }
    },
    async attachEmpresa(empresaId) {
      if (!this.selectedUser || !this.isSuperUser) return;
      this.saving = true;
      try {
        await http.post('/user-company/attach', {
          user_id: this.selectedUser.id,
          empresa_id: empresaId,
        });
        if (!this.selectedEmpresaIds.includes(Number(empresaId))) {
          this.selectedEmpresaIds.push(Number(empresaId));
        }
      } catch (e) {
        this.showAlert('error', 'Erro', 'Não foi possível vincular a empresa.');
      } finally {
        this.saving = false;
      }
    },
    async detachEmpresa(empresaId) {
      if (!this.selectedUser || !this.isSuperUser) return;
      this.saving = true;
      try {
        await http.delete('/user-company/detach', {
          data: {
            user_id: this.selectedUser.id,
            empresa_id: empresaId,
          },
        });
        this.selectedEmpresaIds = this.selectedEmpresaIds.filter((id) => id !== Number(empresaId));
      } catch (e) {
        this.showAlert('error', 'Erro', 'Não foi possível remover o vínculo.');
      } finally {
        this.saving = false;
      }
    },
    async saveAll() {
      if (!this.selectedUser || !this.isSuperUser) return;
      this.saving = true;
      try {
        await http.put(`/user-company/${this.selectedUser.id}/sync`, {
          empresa_ids: this.selectedEmpresaIds,
        });
        this.showAlert('success', 'Sucesso', 'Relacionamentos atualizados.');
        await this.loadData();
      } catch (e) {
        this.showAlert('error', 'Erro', 'Não foi possível salvar os relacionamentos.');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.user-list-scroll {
  max-height: 520px;
  overflow: auto;
}
</style>

